import {Component, Input} from '@angular/core';
import {FamilyMemberResponse, MembershipResponse, UserResponse} from '../../../../@shared/models/response.module';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {AddMembership} from './components/add-membership/add-membership';
import {PurchaseMembership} from './components/purchase-membership/purchase-membership';
import {AQH_FAM_0, AQH_MEM_0, AQH_MEM_3} from '../../../../@shared/models/input.module';
import {MembershipService} from '../../../../@shared/services/membership-service';
import {formatTimeYMD} from '../../../../@core/utils/utils.service';
import {FamilyService} from '../../../../@shared/services/family-service';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-user-profile-membership',
  standalone: false,
  templateUrl: './user-profile-membership.html',
  styleUrl: './user-profile-membership.css'
})
export class UserProfileMembership {
  @Input() user: UserResponse
  memberShips: MembershipResponse[]
  expandedFamilies: { [key: number]: boolean } = {};

  constructor(private modal: NgbModal,
              private membershipService: MembershipService,
              private familyService: FamilyService,
              private translateService: TranslateService) {
  }

  ngOnInit() {
    this.getUserMembership()
  }

  openMembershipModal(familyId: number, existingMembers: FamilyMemberResponse[] = []) {
    let modalRef = this.modal.open(AddMembership, {size: 'lg', centered: true});
    modalRef.componentInstance.familyId = familyId;
    modalRef.componentInstance.existingMembers = existingMembers;
    modalRef.componentInstance.currentUserId = this.user?.user_id || '';
    modalRef.result.then((result) => {
      if (result) {
        this.getUserMembership()
      }
    }).catch(() => {
      // Modal dismissed - still refresh in case members were removed
      this.getUserMembership();
    });
  }

  getUserMembership() {
    let params: AQH_MEM_0 = {}

    this.membershipService.getUserMembership(params).subscribe((res) => {
      if (res) {
        this.memberShips = res;
      }
      else {
        this.memberShips = [];
      }
    })
  }

  isExpiredByDate(membership: MembershipResponse): boolean {
    if (!membership.expires_at) return false;
    const now = new Date();
    const expiry = new Date(membership.expires_at);
    return expiry < now;
  }

  getActiveMemberships(): MembershipResponse[] {
    // Active: status is 'active' and not expired by date
    return this.memberShips?.filter(m => m.status === 'active' && !this.isExpiredByDate(m)) || [];
  }

  getExpiredMemberships(): MembershipResponse[] {
    // Expired: either status is 'expired' OR (status is 'active' but expired by date)
    return this.memberShips?.filter(m =>
      m.status === 'expired' || (m.status === 'active' && this.isExpiredByDate(m))
    ) || [];
  }

  getInactiveMemberships(): MembershipResponse[] {
    // Cancelled or other non-active/non-expired statuses
    return this.memberShips?.filter(m => m.status !== 'active' && m.status !== 'expired') || [];
  }

  removeMemberFromFamily(membership: MembershipResponse, member: FamilyMemberResponse) {
    let payload: AQH_FAM_0 = {
      family_id: membership.family?.family_id!,
      user_id: member.user.user_id
    }
    this.familyService.removeFamilyMember(payload).subscribe((res) => {
      this.getUserMembership()
    })
  }

  openPurchaseMembershipModal() {
    let modalRef = this.modal.open(PurchaseMembership, {size: 'lg', centered: true});
    modalRef.result.then((result) => {
      if (result) {
        this.getUserMembership()
      }
    }).catch(() => {});
  }

  renewMembership(membership: MembershipResponse) {
    if (!membership.plan) return;

    let modalRef = this.modal.open(PurchaseMembership, {size: 'lg', centered: true});
    modalRef.componentInstance.renewPlan = membership.plan;
    modalRef.result.then((result) => {
      if (result) {
        this.getUserMembership()
      }
    }).catch(() => {});
  }

  cancelMembership(membership: MembershipResponse) {
    if (!confirm(this.translateService.instant('membership.cancelConfirm'))) {
      return;
    }

    const payload: AQH_MEM_3 = {
      membership_id: membership.membership_id
    };

    this.membershipService.cancelMembership(payload).subscribe({
      next: () => {
        this.getUserMembership();
      }
    });
  }

  getDaysRemaining(membership: MembershipResponse): number {
    if (!membership.expires_at) return 0;
    const now = new Date();
    const expiry = new Date(membership.expires_at);
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }

  getMembershipProgress(membership: MembershipResponse): number {
    if (!membership.started_at || !membership.expires_at) return 0;
    const start = new Date(membership.started_at).getTime();
    const end = new Date(membership.expires_at).getTime();
    const now = new Date().getTime();
    const total = end - start;
    const elapsed = now - start;
    const progress = (elapsed / total) * 100;
    return Math.min(100, Math.max(0, progress));
  }

  isExpiringSoon(membership: MembershipResponse): boolean {
    if (membership.status !== 'active') return false;
    const daysRemaining = this.getDaysRemaining(membership);
    return daysRemaining <= 30 && daysRemaining > 0;
  }

  getInitials(name: string): string {
    if (!name) return '';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  }

  isFamilyFull(membership: MembershipResponse): boolean {
    if (!membership.plan?.max_children || !membership.family) return false;
    return membership.family.members.length >= membership.plan.max_children;
  }

  toggleFamily(index: number): void {
    this.expandedFamilies[index] = !this.expandedFamilies[index];
  }

  protected readonly formatTimeYMD = formatTimeYMD;
}
